/** Sparse native inland savanna, generated in terrain workers. The candidate
 * lattice belongs to the planet, never to a streamed tile or camera view. */
import {createSurfaceColumn,FACES,hashSeed,rngFromString,seaSurface,surfaceColumnFromSample,
  type PlanetDef,type PlanetSampler,type TerrainSample} from '@hyperlight/shared';
import {groundSavannaInland,groundSnow,groundDuneSuppression,hasLivingGround} from '../groundSurface';
import {clearingAt} from './field';

type V3=readonly [number,number,number];
export interface SavannaTile {face:number;level:number;u0:number;v0:number;size:number}
export interface SavannaPlacement {
  id:string;kind:'acacia';face:number;u:number;v:number;dir:V3;normal:V3;
  radius:number;size:number;yaw:number;variant:number;priority:number;
  temp:number;moist:number;depth:number;weight:number;grove:number;footprint:number;relief:number;
}
export type SavannaData=SavannaPlacement[];
export const SAVANNA_LIMIT=32;
/** At full suitability the Jacobian-corrected lattice supports approximately
 * 20–50 trees/km²: occasional individuals between loose, broader groves. */
export const SAVANNA_SPACING_M=110;
const column=createSurfaceColumn(),coast={weight:0,heightM:0,beach:0,rock:0};
const fresh=():TerrainSample=>({h:0,temp:0,moist:0,river:0,mountain:0,town:0,road:8,flowx:0,flowy:0,flowz:0});
const smooth=(a:number,b:number,x:number):number=>{const t=Math.max(0,Math.min(1,(x-a)/(b-a)));return t*t*(3-2*t);};
const norm=(x:number,y:number,z:number):V3=>{const l=Math.hypot(x,y,z);return [x/l,y/l,z/l];};
const seeds=new WeakMap<PlanetDef,number>();
function seed(def:PlanetDef):number {let s=seeds.get(def);if(s===undefined){s=hashSeed(`${def.seed}:savanna-groves:1`);seeds.set(def,s);}return s;}

/** Physical and ecological permission, shared with the ordinary canopy mask.
 * The inland pigment transition is the same CPU/GLSL field as the ground. */
export function savannaAllowed(def:PlanetDef,sampler:PlanetSampler,dir:V3,ts:TerrainSample,footprint=0):number {
  if(!hasLivingGround(def)||(def.seaRadius!==null&&seaSurface(def)!=='water')||ts.town>.002
    ||ts.road<3+footprint||ts.river>.18||(ts.glacierIceM??0)>.02||(ts.lavaDepthM??0)>0)return 0;
  const height=ts.h*sampler.amplitude,above=def.radius+height-(def.seaRadius??-1e9);
  const weight=groundSavannaInland(ts.temp,ts.moist,above);
  if(weight<=.015||groundSnow(ts.temp,height,def.climate.snowline*sampler.amplitude)>.04)return 0;
  if(surfaceColumnFromSample(def,sampler.amplitude,ts,column).waterDepth>-8)return 0;
  // The whole finite native coast receives its own habitat: no acacias mixed
  // into palm colonies, exposed strandlines, headlands or sheltered banks.
  if((ts.estuaryShelter??0)>.06||sampler.coastalAt(...dir,coast).weight>.025)return 0;
  return weight;
}

/** Trees of the ordinary forest recede throughout the suitable savanna, so
 * empty spaces between acacia groups remain grassland instead of refilling. */
export function savannaForestWeight(def:PlanetDef,sampler:PlanetSampler,dx:number,dy:number,dz:number,ts:TerrainSample):number {
  const weight=savannaAllowed(def,sampler,[dx,dy,dz],ts);
  return smooth(.015,.68,weight);
}

const PROBES:readonly (readonly [number,number])[]=[[1,0],[-1,0],[0,1],[0,-1],
  [Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,-Math.SQRT1_2],[Math.SQRT1_2,-Math.SQRT1_2]];

export function buildSavannaData(def:PlanetDef,sampler:PlanetSampler,tile:SavannaTile):SavannaData {
  const result:SavannaData=[];
  const suppressed=new Set<string>();
  if(!hasLivingGround(def)||(def.seaRadius!==null&&seaSurface(def)!=='water'))return result;
  const grid=Math.max(4,Math.ceil(2*def.radius/SAVANNA_SPACING_M)),step=2/grid;
  const i0=Math.max(0,Math.floor((tile.u0+1)/step)),i1=Math.min(grid,Math.ceil((tile.u0+tile.size+1)/step));
  const j0=Math.max(0,Math.floor((tile.v0+1)/step)),j1=Math.min(grid,Math.ceil((tile.v0+tile.size+1)/step));
  const basis=FACES[tile.face],ts=fresh(),pt=fresh(),A=sampler.amplitude;
  for(let j=j0;j<j1;j++)for(let i=i0;i<i1;i++){
    const id=`${def.seed}:savanna1:${tile.face}:${i}:${j}`,rng=rngFromString(id);
    const u=-1+(i+.18+rng()*.64)*step,v=-1+(j+.18+rng()*.64)*step;
    if(u<tile.u0||u>=tile.u0+tile.size||v<tile.v0||v>=tile.v0+tile.size)continue;
    const dir=norm(...basis.normal.map((n,k)=>n+basis.right[k]*u+basis.up[k]*v) as [number,number,number]);
    const chance=rng(),size=.86+rng()*.28,yaw=rng()*Math.PI*2,variant=Math.floor(rng()*4),priority=rng();
    // A cube's projected square shrinks toward its edges. Weight by its true
    // spherical area so a face seam cannot turn into a denser line of trees.
    const jacobian=1/Math.pow(1+u*u+v*v,1.5);
    const grove=smooth(.27,.72,clearingAt(...dir,def.radius/390,seed(def)));
    if(chance>.80*jacobian*(.28+.72*grove))continue;
    sampler.sample(...dir,ts,true);
    const footprint=1.25*size,weight=savannaAllowed(def,sampler,dir,ts,footprint);
    if(weight===0)continue;
    // Mild drainage supports fuller groves, while the shared signed-water
    // check still leaves eight metres of dry support above the local water.
    const drainage=1+.25*smooth(.015,.12,ts.river);
    if(chance>.64*jacobian*(.28+.72*grove)*weight*drainage)continue;
    const [x,y,z]=dir,ex=Math.abs(y)<.9?norm(-z,0,x):norm(0,z,-y);
    const ez:V3=[y*ex[2]-z*ex[1],z*ex[0]-x*ex[2],x*ex[1]-y*ex[0]],radius=def.radius+ts.h*A;
    const heights:number[]=[];let low=0,high=0,valid=true;
    for(const [a,b] of PROBES){
      const d=norm(...dir.map((n,k)=>n+(ex[k]*a+ez[k]*b)*footprint/radius) as [number,number,number]);
      sampler.sample(...d,pt,true);
      if(savannaAllowed(def,sampler,d,pt)===0){valid=false;break;}
      const delta=(pt.h-ts.h)*A;heights.push(delta);low=Math.min(low,delta);high=Math.max(high,delta);
    }
    if(!valid||high-low>.8)continue;
    const gx=(heights[0]-heights[1])/(2*footprint),gz=(heights[2]-heights[3])/(2*footprint);
    if(Math.hypot(gx,gz)>.38)continue;
    result.push({id,kind:'acacia',face:tile.face,u,v,dir,normal:dir,
      radius:radius+Math.min(0,low+.10*size)-.035,size,yaw,variant,priority,temp:ts.temp,moist:ts.moist,
      depth:surfaceColumnFromSample(def,A,ts,column).waterDepth,weight,grove,footprint,relief:high-low});
    const duneSuppression=groundDuneSuppression(ts.duneSand??0,'tree');
    if(duneSuppression>0&&rngFromString(`${id}:dune`)()<duneSuppression)suppressed.add(id);
  }
  result.sort((a,b)=>a.priority-b.priority||(a.id<b.id?-1:a.id>b.id?1:0));
  // Filter the original capped cohort, keeping every unaffected tree identity
  // and exact forest/far cohort parity instead of refilling removed trees.
  return result.slice(0,SAVANNA_LIMIT).filter(p=>!suppressed.has(p.id));
}

/** Exact child cohorts preserve ownership, transforms and the cap at distance. */
export function buildSavannaFarData(def:PlanetDef,sampler:PlanetSampler,tile:SavannaTile):SavannaData {
  const result:SavannaData=[],size=tile.size*.5;
  for(let j=0;j<2;j++)for(let i=0;i<2;i++)result.push(...buildSavannaData(def,sampler,
    {face:tile.face,level:tile.level+1,u0:tile.u0+i*size,v0:tile.v0+j*size,size}));
  return result;
}
